import { useMemo } from "react";
import { useAtomValue } from "jotai";
import {
  activeFlowIdAtom,
  dynamicNodeConfigsAtom,
  nodeDataMapAtom,
  nodeRegistryAtom,
} from "@/store/editor-v2";
import { useSpotUser } from "@/utils/spotUser";
import { useSecretsSources } from "@/components/SpotflowV2/Sidebar/useSecretsSources";
import { getCompatibility } from "@/components/SpotflowV2/Sidebar/typeCompatibility";
import {
  resolveBindingTarget,
  type SecretLookupEntry,
} from "@/components/SpotflowV2/Sidebar/mentionSource";
import type { NodeV2Data } from "@/types/spotflow-v2";
import type { PickerVisibleInput } from "./useNodeInputs";

export interface BoundInput extends PickerVisibleInput {
  sourceNodeId: string;
  sourceName: string;
  sourceOutput: string;
  sourceImage: string | undefined;
  sourceColor: string | undefined;
  /** True when the binding source is a workflow- or workspace-scoped secret. */
  isSecret: boolean;
  sourceIsList: boolean;
  sourceDataType: string | undefined;
  bindingKey: string;
  isIncompatible: boolean;
}

// Element-type half of the shared truth table; coerced matches don't warn.
function isTypeCompatible(
  sourceType: string | undefined,
  targetType: string | undefined
): boolean {
  return getCompatibility(sourceType, targetType, 0, 0) !== "incompatible";
}

/**
 * Resolve all variable bindings for a node's visible inputs.
 * Walks upstream nodes to determine list propagation status.
 */
export function useNodeBindings(
  data: NodeV2Data,
  pickerVisibleInputs: PickerVisibleInput[]
): { boundInputs: BoundInput[]; receivesListInput: boolean } {
  const nodeDataMap = useAtomValue(nodeDataMapAtom);
  const nodeRegistry = useAtomValue(nodeRegistryAtom);
  const dynamicNodeConfigs = useAtomValue(dynamicNodeConfigsAtom);
  const { spotGroup } = useSpotUser();
  const activeFlowId = useAtomValue(activeFlowIdAtom);
  const { secrets } = useSecretsSources(spotGroup?.id, activeFlowId);
  const secretsByQualifiedId = useMemo(() => {
    const m = new Map<string, SecretLookupEntry>();
    for (const s of secrets) m.set(`${s.scope}:${s.id}`, s);
    return m;
  }, [secrets]);

  const boundInputs = useMemo(() => {
    const bindings = data.variableBindings ?? {};

    const resolveSource = (sourceNodeId: string, outputName: string) =>
      resolveBindingTarget(
        sourceNodeId,
        outputName,
        nodeDataMap,
        nodeRegistry,
        secretsByQualifiedId,
        dynamicNodeConfigs
      );

    const checkIncompatible = (
      inp: PickerVisibleInput,
      sourceDataType: string | undefined
    ): boolean => {
      if (!sourceDataType) return false;
      // Connectors that declare a `choices` list (variable_type_connector,
      // variable_type_size_connector) accept any source whose type matches one
      // of their choices — the runtime coerces between them.
      const choices = inp.choices ?? [];
      if (choices.length > 0) {
        return !choices.some((c) =>
          isTypeCompatible(sourceDataType, c.data_type)
        );
      }
      return !isTypeCompatible(sourceDataType, inp.dataType);
    };

    const results: BoundInput[] = [];

    for (const inp of pickerVisibleInputs) {
      // Direct binding
      const ref = bindings[inp.name] as string | undefined;
      if (ref) {
        const [sourceNodeId, outputName] = ref.split(".");
        const source = resolveSource(sourceNodeId, outputName ?? "output");
        results.push({
          ...inp,
          ...source,
          bindingKey: inp.name,
          isIncompatible: checkIncompatible(inp, source.sourceDataType),
        });
        continue;
      }
      // Variable connector keyed bindings — collect ALL
      const prefix = `${inp.name}__`;
      const keyedEntries = Object.keys(bindings).filter(
        (k) => k.startsWith(prefix) && bindings[k]
      );
      for (const key of keyedEntries) {
        const ref2 = bindings[key] as string;
        const [sourceNodeId, outputName] = ref2.split(".");
        const source = resolveSource(sourceNodeId, outputName ?? "output");
        results.push({
          ...inp,
          ...source,
          bindingKey: key,
          isIncompatible: checkIncompatible(inp, source.sourceDataType),
        });
      }
    }

    return results;
  }, [
    pickerVisibleInputs,
    data.variableBindings,
    nodeDataMap,
    nodeRegistry,
    secretsByQualifiedId,
    dynamicNodeConfigs,
  ]);

  const receivesListInput = boundInputs.some((b) => b.sourceIsList);

  return { boundInputs, receivesListInput };
}
